// process.env safe client component
import React, { useState, useEffect, useCallback } from 'react';
import {
  Bot,
  Layers,
  Layout,
  Compass,
  FileText,
  Shield,
  Sparkles,
  ArrowRight,
  CheckCircle2,
  ExternalLink,
  Lock,
  User,
  LogOut,
  ChevronRight,
  Activity,
  Zap,
  Globe,
  RefreshCw,
  Sliders,
  Send,
  MessageSquare,
  HelpCircle
} from 'lucide-react';

import { LunaAuthWidget } from '../auth/LunaAuthWidget';

interface PortalBlock {
  id: string;
  moduleId: string;
  nodeId: string;
  title: string;
  route: string;
  colSpan?: 12 | 8 | 6 | 4;
  style?: string;
  customInstructions?: string;
  isVisible?: boolean;
}

interface PortalData {
  tenantName: string;
  themeColor: string;
  showBranding: boolean;
  publishedAt?: string;
  layout?: {
    theme?: {
      mode?: string;
      primaryColor?: string;
      accentColor?: string;
      heroTitle?: string;
      heroSubtitle?: string;
      showBranding?: boolean;
    };
    blocks?: PortalBlock[];
  };
}

export const LiveClientPortal: React.FC = () => {
  // ── 1. Discover Tenant Key from Subdomain or URL ────────────────────────
  const [tenantKey, setTenantKey] = useState<string>(() => {
    if (typeof window !== 'undefined') {
      const host = window.location.hostname.toLowerCase();
      if (host.endsWith('.kangavena.com') && 
          !host.startsWith('pos.') && 
          !host.startsWith('admin.') && 
          !host.startsWith('www.') && 
          !host.startsWith('app.') && 
          !host.startsWith('api.')) {
        const sub = host.split('.')[0];
        if (sub) return sub;
      }
    }
    const pathParts = window.location.pathname.split('/').filter(Boolean);
    // e.g. /portal/TENET_123 or /portal/TENET_123/tax-filing or /portal/mybusiness
    if (pathParts.length >= 2 && pathParts[0] === 'portal' && pathParts[1] !== 'login') {
      return pathParts[1];
    }
    const params = new URLSearchParams(window.location.search);
    return params.get('tenantId') || params.get('apiKey') || 'TENET_C80BF73C880C85D03C0BCD42BE2CA2C6';
  });

  // ── 2. Client Authentication State (Zero-Trust Session) ──────────────────
  const [clientUser, setClientUser] = useState<any>(() => {
    try {
      const saved = sessionStorage.getItem('client_portal_user');
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  });

  // Check active server-side JWT cookie on initial load
  useEffect(() => {
    fetch('/api/tenet/end-user/auth/me', {
      credentials: 'include'
    })
    .then(async (res) => {
      if (res.ok) {
        const data = await res.json();
        if (data.success && data.user) {
          const profile = {
            id: data.user.id,
            name: data.user.fullName,
            email: data.user.email,
            role: data.user.role || 'Verified Corporate Client',
            tenantId: data.user.tenantId,
            signedInAt: data.user.lastLoginAt || new Date().toISOString()
          };
          sessionStorage.setItem('client_portal_user', JSON.stringify(profile));
          setClientUser(profile);
        }
      }
    })
    .catch(() => {});
  }, []);

  // ── 3. Portal Configuration & Active Route ──────────────────────────────
  const [portalData, setPortalData] = useState<PortalData | null>(null);
  const [loadingPortal, setLoadingPortal] = useState<boolean>(true);
  const [activeRoute, setActiveRoute] = useState<string>(() => {
    return window.location.pathname.startsWith('/portal') && window.location.pathname.split('/').length > 3
      ? `/${window.location.pathname.split('/').slice(3).join('/')}`
      : '/'
  });

  // ── 4. Inject Luna AI Embed Script dynamically ──────────────────────────
  useEffect(() => {
    if (!tenantKey) return;

    // Check if script is already present
    const existingScript = document.querySelector('script[src*="luna-agent.js"]');
    if (!existingScript) {
      const script = document.createElement('script');
      script.src = `https://kangavena.com/luna-agent.js?tenantId=${encodeURIComponent(tenantKey)}`;
      script.setAttribute('data-tenant-id', tenantKey);
      script.defer = true;
      document.body.appendChild(script);
    }
  }, [tenantKey]);

  // ── 5. Fetch Portal Layout from Zero-Trust Public API ───────────────────
  const loadPortalConfig = useCallback(async () => {
    if (!tenantKey) return;
    setLoadingPortal(true);
    try {
      const res = await fetch(`/api/tenet/public/portal/${encodeURIComponent(tenantKey)}`);
      const data = await res.json();
      if (data.success && data.portal) {
        setPortalData(data.portal);
      }
    } catch (err) {
      console.warn('[LiveClientPortal] Failed to fetch portal configuration:', err);
    } finally {
      setLoadingPortal(false);
    }
  }, [tenantKey]);

  useEffect(() => {
    loadPortalConfig();
  }, [loadPortalConfig]);

  // ── 6. Handle Navigation & Synchronize Luna AI Route ─────────────────────
  const navigateToSection = (targetRoute: string) => {
    setActiveRoute(targetRoute);
    
    // Update window URL
    const fullPortalPath = `/portal/${tenantKey}${targetRoute === '/' ? '' : targetRoute}`;
    window.history.pushState({}, '', fullPortalPath);

    // Dispatch popstate for Luna Route Observer
    window.dispatchEvent(new PopStateEvent('popstate'));

    // Trigger Luna Agent directly if hook available
    if ((window as any).LunaAgent && typeof (window as any).LunaAgent.setRoute === 'function') {
      (window as any).LunaAgent.setRoute(targetRoute);
    } else {
      window.dispatchEvent(new CustomEvent('LUNA_TRIGGER_ROUTE', { detail: { route: targetRoute } }));
    }
  };

  const handleLogout = async () => {
    try {
      await fetch('/api/tenet/end-user/auth/logout', {
        method: 'POST',
        credentials: 'include'
      });
    } catch {}
    sessionStorage.removeItem('client_portal_user');
    setClientUser(null);
  };

  const blocks: PortalBlock[] = portalData?.layout?.blocks || [];
  const heroTitle = portalData?.layout?.theme?.heroTitle || `${portalData?.tenantName || 'Enterprise'} Client Concierge Portal`;
  const heroSubtitle = portalData?.layout?.theme?.heroSubtitle || 'Autonomous Tax Strategy, Multi-Currency Banking & Operational Self-Service powered by Luna AI';
  const showBranding = portalData?.showBranding !== false;

  // ═════════════════════════════════════════════════════════════════════════
  // LOGIN SCREEN (LUNA AUTH GENUI WIDGET - ZERO-TRUST GATEWAY)
  // ═════════════════════════════════════════════════════════════════════════
  if (!clientUser) {
    return (
      <div className="min-h-screen w-full bg-[#060913] text-slate-100 font-sans flex flex-col justify-center items-center p-4 relative overflow-hidden selection:bg-cyan-500/30 selection:text-cyan-200">
        {/* Glowing Background Orbs */}
        <div className="absolute top-[-10%] left-[-10%] w-[500px] h-[500px] bg-cyan-500/10 rounded-full blur-[140px] pointer-events-none" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[600px] h-[600px] bg-indigo-500/10 rounded-full blur-[160px] pointer-events-none" />

        <LunaAuthWidget
          tenantApiKey={tenantKey}
          tenantName={portalData?.tenantName || 'Client Portal'}
          onSuccess={(user) => setClientUser(user)}
        />
      </div>
    );
  }

  // ═════════════════════════════════════════════════════════════════════════
  // LIVE CLIENT PORTAL MAIN DASHBOARD
  // ═════════════════════════════════════════════════════════════════════════
  const activeBlock = blocks.find(b => b.route === activeRoute);

  return (
    <div className="min-h-screen w-screen bg-[#060913] text-slate-100 font-sans flex flex-col md:flex-row selection:bg-cyan-500/30 selection:text-cyan-200 antialiased overflow-hidden">
      
      {/* ── 1. DYNAMIC CLIENT PORTAL SIDEBAR ─────────────────────────────── */}
      <aside className="w-full md:w-64 bg-[#0D121F] border-r border-slate-800/80 flex flex-col justify-between shrink-0 p-4 md:h-screen sticky top-0 z-30 shadow-2xl">
        <div className="space-y-5 overflow-y-auto pr-1 flex-1">
          
          {/* Tenant Brand Identity */}
          <div className="flex items-center gap-3 pb-4 border-b border-slate-800/80">
            <div className="p-2.5 rounded-xl bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 shadow-[0_0_15px_rgba(6,182,212,0.25)]">
              <Bot className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <h3 className="text-xs font-black text-white uppercase tracking-tight truncate font-sans">
                {portalData?.tenantName || 'Enterprise'}
              </h3>
              <p className="text-[10px] text-slate-400 font-mono flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> Client Portal
              </p>
            </div>
          </div>

          {/* Navigation Menu (Dynamically Generated from SectionNodes) */}
          <nav className="space-y-1.5">
            <span className="text-[10px] font-mono uppercase tracking-wider text-slate-400 font-bold px-2 block">
              PORTAL NAVIGATION
            </span>

            {/* Portal Overview Home */}
            <button
              type="button"
              onClick={() => navigateToSection('/')}
              className={`w-full px-3 py-2.5 rounded-xl text-xs font-bold transition flex items-center justify-between cursor-pointer border ${
                activeRoute === '/'
                  ? 'bg-cyan-500/15 border-cyan-500/40 text-cyan-300 shadow-sm'
                  : 'bg-slate-900/40 border-transparent text-slate-400 hover:text-white hover:bg-slate-800/60'
              }`}
            >
              <div className="flex items-center gap-2.5">
                <Layout size={14} className={activeRoute === '/' ? 'text-cyan-400' : 'text-slate-500'} />
                <span>Dashboard Overview</span>
              </div>
              <ChevronRight size={12} className={activeRoute === '/' ? 'text-cyan-400' : 'text-slate-600'} />
            </button>

            {/* Dynamically Populated Sections */}
            {blocks.map((block) => {
              const isActive = activeRoute === block.route;
              return (
                <button
                  key={block.id}
                  type="button"
                  onClick={() => navigateToSection(block.route)}
                  className={`w-full px-3 py-2.5 rounded-xl text-xs font-bold transition flex items-center justify-between cursor-pointer border ${
                    isActive
                      ? 'bg-cyan-500/15 border-cyan-500/40 text-cyan-300 shadow-[0_0_15px_rgba(6,182,212,0.15)]'
                      : 'bg-slate-900/40 border-transparent text-slate-400 hover:text-white hover:bg-slate-800/60'
                  }`}
                >
                  <div className="flex items-center gap-2.5 truncate">
                    <Compass size={14} className={isActive ? 'text-cyan-400' : 'text-slate-500'} />
                    <span className="truncate">{block.title}</span>
                  </div>
                  <span className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-cyan-500/10 text-cyan-300 border border-cyan-500/20 shrink-0">
                    AI
                  </span>
                </button>
              );
            })}
          </nav>

          {/* Telemetry Card */}
          <div className="bg-gradient-to-b from-[#131A2B] to-[#0D121F] border border-cyan-500/20 rounded-2xl p-3.5 space-y-2">
            <div className="flex items-center gap-2">
              <Sparkles className="w-3.5 h-3.5 text-cyan-400 animate-spin" />
              <span className="text-[10px] font-bold uppercase tracking-wider text-cyan-300 font-mono">LUNA AI CONCIERGE</span>
            </div>
            <p className="text-[11px] text-slate-300 leading-relaxed font-sans">
              Autonomous form intake & voice ghost-typing are synchronized with your active session.
            </p>
          </div>
        </div>

        {/* User Profile & Logout */}
        <div className="pt-3 border-t border-slate-800/80 space-y-2 shrink-0">
          <div className="bg-[#131A2B] border border-slate-800 rounded-xl p-2.5 flex items-center justify-between">
            <div className="flex items-center gap-2 min-w-0">
              <div className="w-7 h-7 rounded-lg bg-cyan-500/20 text-cyan-300 flex items-center justify-center font-bold text-xs shrink-0">
                <User size={13} />
              </div>
              <div className="min-w-0">
                <p className="text-xs font-bold text-white truncate">{clientUser.name}</p>
                <p className="text-[10px] text-slate-400 truncate">{clientUser.role}</p>
              </div>
            </div>
            <button
              type="button"
              onClick={handleLogout}
              className="p-1.5 rounded-lg text-slate-400 hover:text-rose-300 hover:bg-rose-500/10 transition cursor-pointer"
              title="Sign Out"
            >
              <LogOut size={13} />
            </button>
          </div>
        </div>
      </aside>

      {/* ── 2. MAIN PORTAL CANVAS ────────────────────────────────────────── */}
      <main className="flex-1 h-screen overflow-y-auto bg-gradient-to-b from-[#070A12] to-[#090D16] p-6 sm:p-8 space-y-6 relative">
        
        {/* Top Floating Action HUD */}
        <div className="flex items-center justify-between pb-4 border-b border-slate-800/60">
          <div className="flex items-center gap-2">
            <span className="text-xs font-mono text-cyan-400 font-bold flex items-center gap-1.5">
              <Shield size={14} /> SECURE CLIENT ACCESS
            </span>
            <span className="text-slate-600">/</span>
            <span className="text-xs font-mono text-slate-300">
              {activeRoute === '/' ? 'Dashboard' : activeBlock?.title || activeRoute}
            </span>
          </div>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigateToSection(activeRoute)}
              className="px-3.5 py-1.5 rounded-xl text-xs font-bold bg-cyan-500/15 hover:bg-cyan-500/25 text-cyan-300 border border-cyan-500/30 flex items-center gap-1.5 transition cursor-pointer shadow-sm"
            >
              <Sparkles size={13} />
              <span>Trigger Luna GenUI</span>
            </button>
          </div>
        </div>

        {/* Hero Banner */}
        <div className="rounded-3xl bg-[#0D1324]/90 border border-cyan-500/30 p-6 sm:p-8 backdrop-blur-xl shadow-2xl relative overflow-hidden space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-[10px] uppercase tracking-widest text-cyan-400 font-mono font-bold flex items-center gap-1.5">
              <Zap size={13} /> ACTIVE CLIENT REPOSITORY
            </span>
            <span className="text-emerald-400 text-xs font-mono flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> Live WebSocket Agent
            </span>
          </div>

          <h1 className="text-2xl sm:text-3xl font-black text-white tracking-tight font-sans">
            {activeBlock ? activeBlock.title : heroTitle}
          </h1>
          <p className="text-xs sm:text-sm text-slate-300 max-w-2xl leading-relaxed">
            {activeBlock 
              ? `Engage directly with the ${activeBlock.title} workflow. Luna AI dynamically mounts interactive schemas and handles voice ghost-typing for route ${activeBlock.route}.`
              : heroSubtitle}
          </p>
        </div>

        {/* Dynamic Section Cards Grid */}
        <div className="grid grid-cols-12 gap-5">
          {blocks.map((block) => {
            const isCurrent = activeRoute === block.route;
            const colSpanClass =
              block.colSpan === 12
                ? 'col-span-12'
                : block.colSpan === 8
                ? 'col-span-12 lg:col-span-8'
                : block.colSpan === 6
                ? 'col-span-12 md:col-span-6'
                : 'col-span-12 sm:col-span-6 lg:col-span-4';

            return (
              <div
                key={block.id}
                onClick={() => navigateToSection(block.route)}
                className={`${colSpanClass} rounded-3xl p-6 transition-all duration-300 cursor-pointer group ${
                  isCurrent
                    ? 'bg-[#11182B] border-2 border-cyan-400 shadow-[0_0_35px_rgba(6,182,212,0.3)]'
                    : 'bg-[#0D121F]/90 hover:bg-[#12192D] border border-cyan-500/20 hover:border-cyan-400/50 shadow-xl'
                }`}
              >
                <div className="flex items-center justify-between pb-4 border-b border-white/5">
                  <div className="flex items-center gap-3">
                    <div className="p-2.5 rounded-2xl bg-cyan-500/15 border border-cyan-400/30 text-cyan-300 group-hover:scale-105 transition">
                      <Compass size={18} />
                    </div>
                    <div>
                      <h3 className="text-sm font-black text-white group-hover:text-cyan-300 transition">
                        {block.title}
                      </h3>
                      <span className="font-mono text-[10px] text-cyan-300/80">
                        {block.route}
                      </span>
                    </div>
                  </div>
                  <ArrowRight size={16} className="text-slate-500 group-hover:text-cyan-400 group-hover:translate-x-1 transition" />
                </div>

                <div className="pt-4 space-y-3">
                  <p className="text-xs text-slate-300 leading-relaxed font-sans">
                    Click to launch this module. Luna AI automatically connects to the underlying section persona and prepares dynamic GenUI intake.
                  </p>
                  <div className="flex items-center justify-between text-[11px] pt-1">
                    <span className="text-slate-400 font-medium">GenUI Status:</span>
                    <span className="text-emerald-400 font-mono font-bold flex items-center gap-1">
                      <CheckCircle2 size={12} /> Ready for Voice/Chat
                    </span>
                  </div>
                </div>
              </div>
            );
          })}

          {blocks.length === 0 && !loadingPortal && (
            <div className="col-span-12 rounded-3xl bg-[#0D121F]/60 border border-white/10 p-12 text-center space-y-3">
              <Bot size={36} className="mx-auto text-cyan-400/60" />
              <h3 className="text-sm font-black text-white">No Modules Published Yet</h3>
              <p className="text-xs text-slate-400 max-w-md mx-auto">
                The Tenant Admin is currently designing the Client Portal layout using the Tenet Portal Builder.
              </p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};
